// Libs
import classNames from 'classnames/bind';
import { useSelector } from 'react-redux';
import { Box, Button, Stack, Typography } from '@mui/material';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
// Components, Layouts, Pages
import {
  BaseAvatar,
  BaseFilters,
  BaseInputSearch,
  BaseMoreAction,
  BaseSelect,
  BaseStatus,
  BaseTable,
  BaseToolbar,
  ConfirmModal,
  Loading,
  SortComponent,
  UnderDevelopment,
} from '~/components/common';
// Others
import { IQueryParamUser, IUser } from '~/utils/interface/user';
import { getFullName } from '~/utils/helper/common';
import { IPaginationResponse } from '~/utils/interface/base';
import {
  IBaseMoreAction,
  IBaseOption,
  IBodyUpdate,
  IFilterValueChange,
  IHeaderCellType,
  IPayloadCallApiFn,
  IQueryParamBase,
  IResultCallApiFn,
  ISortBy,
} from '~/utils/interface/common';
import {
  DEFAULT_CURRENT_PAGE,
  DEFAULT_DEBOUNCE_DELAY_TIME,
  DEFAULT_LIMIT_PAGE,
  DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA,
  DEFAULT_SORT_USER,
  EMPTY_STRING,
  FILTER_DEFAULT_VALUES,
  optionsStatusUser,
} from '~/utils/constants/common';
import { RoleEnum, StatusEnum } from '~/utils/enum/common';
import { StorageEnum } from '~/utils/enum/base';
import { adminRouteAbsolute } from '~/utils/routes/roleRoutes/admin';
import useDebounce from '~/utils/hooks/useDebounce';
import { useAppDispatch } from '~/utils/redux/hooks';
import { deleteUser, getListUser, updateUser } from '~/thunks/user/userThunk';
import { salesAdminRouteAbsolute } from '~/utils/routes/roleRoutes/salesAdmin';
import { salesPersonRouteAbsolute } from '~/utils/routes/roleRoutes/salesPerson';
import { operationsManagerRouteAbsolute } from '~/utils/routes/roleRoutes/operationsManager';
import { warehouseLeadRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseLead';
import { warehouseStaffRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseStaff';
import { foremanRouteAbsolute } from '~/utils/routes/roleRoutes/foreman';
import { crewRouteAbsolute } from '~/utils/routes/roleRoutes/crew';
import { selectIsRefreshListUser, userActions } from '~/thunks/user/userSlice';
import { loaderRouteAbsolute } from '~/utils/routes/roleRoutes/loader';
import { usePermissions } from '~/utils/hooks/userPermissions';
import { USER_PERMISSIONS } from '~/utils/constants/permissions';
// Styles, images, icons
import styles from './User.module.scss';
import { icons } from '~/assets';
import { getListRole } from '~/thunks/role/roleThunk';

type Props = {};

const cx = classNames.bind(styles);

const columnsUsers = (
  t: TFunction,
  sortBy: ISortBy[],
  handleSort: (sortType: string, name: string) => void,
  onView: (id: string, item: IUser) => void,
  onEdit: (id: string) => void,
  onStatus: (item: IUser) => void,
  onDelete: (id: string) => void,
  isDetail: boolean,
  isEdit: boolean,
  isDelete: boolean
): IHeaderCellType<IUser>[] => {
  const getSortType = (name: string) => sortBy.find((item) => item.name === name)?.typeSort || EMPTY_STRING;

  return [
    {
      id: 'id',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_user_id')}</Box>
          <SortComponent name='id' sortType={getSortType('id')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IUser) => record?.id,
    },
    {
      id: 'user',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_user')}</Box>
          <SortComponent name='name' sortType={getSortType('name')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IUser) => {
        return (
          <Stack direction={'row'} gap={4} alignItems={'center'}>
            <BaseAvatar dataAccount={record} size={32} fontSize={14} />
            <Stack direction={'column'}>
              <Typography className={cx('textName')}>
                {getFullName({
                  firstName: record?.firstName,
                  lastName: record?.lastName,
                })}
              </Typography>
              <Typography className={cx('textEmail')}>{record?.email}</Typography>
            </Stack>
          </Stack>
        );
      },
    },
    {
      id: 'role',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_role')}</Box>
          <SortComponent name='role' sortType={getSortType('role')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IUser) => {
        return <>{record?.role?.join(', ')}</>;
      },
    },
    {
      id: 'status',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_status')}</Box>
          <SortComponent name='status' sortType={getSortType('status')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IUser) => <BaseStatus label={record?.status || EMPTY_STRING} />,
    },
    {
      id: 'action',
      label: t('table_header_title_actions'),
      minWidth: 100,
      align: 'center',
      render: (record: IUser) => {
        const currentRole: string = JSON.parse(localStorage.getItem(StorageEnum.ROLE)!)[0] ?? EMPTY_STRING;
        const accountId: string = localStorage.getItem(StorageEnum.ACCOUNT_ID) ?? EMPTY_STRING;

        const actions: IBaseMoreAction[] = [
          ...(isDetail
            ? [
                {
                  label: t('table_action_view', { ns: 'common/base' }),
                  icon: (
                    <img
                      src={icons.tableIconActionView}
                      alt={t('common_alt_img', { ns: 'common/base' })}
                      width={16}
                      height={16}
                    />
                  ),
                  onClick: () => onView(record.id, record),
                },
              ]
            : []),
          ...((record.id === accountId &&
            (currentRole === RoleEnum.SUPER_ADMIN || currentRole === RoleEnum.SALES_ADMIN)) ||
          (currentRole === RoleEnum.SUPER_ADMIN && record?.role && record?.role[0] !== RoleEnum.SUPER_ADMIN)
            ? [
                {
                  label: t('table_action_edit', { ns: 'common/base' }),
                  icon: (
                    <img
                      src={icons.tableIconActionEdit}
                      alt={t('common_alt_img', { ns: 'common/base' })}
                      width={16}
                      height={16}
                    />
                  ),
                  onClick: () => onEdit(record.id),
                },
                {
                  label: t(record.status === StatusEnum.ACTIVE ? 'table_action_inactive' : 'table_action_active', {
                    ns: 'common/base',
                  }),
                  icon: (
                    <img
                      src={icons.tableIconActionActivate}
                      alt={t('common_alt_img', { ns: 'common/base' })}
                      width={16}
                      height={16}
                    />
                  ),
                  hasOtherColor: true,
                  onClick: () => onStatus(record),
                },
              ]
            : []),

          ...(record.id !== accountId &&
          currentRole === RoleEnum.SUPER_ADMIN &&
          record?.role &&
          record?.role[0] !== RoleEnum.SUPER_ADMIN
            ? [
                {
                  label: t('table_action_delete', { ns: 'common/base' }),
                  icon: (
                    <img
                      src={icons.tableIconActionDelete}
                      alt={t('common_alt_img', { ns: 'common/base' })}
                      width={16}
                      height={16}
                    />
                  ),
                  onClick: () => onDelete(record.id),
                  hasOtherColor: true,
                },
              ]
            : []),
        ];

        return <BaseMoreAction actions={actions} />;
      },
    },
  ];
};

const User = (props: Props) => {
  //#region Destructuring Props
  const {} = props;

  const { canCreateUsers, canViewUsers, canDetailUsers, canEditUsers, canDeleteUsers } =
    usePermissions(USER_PERMISSIONS);
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/users', 'common/base']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => Object.fromEntries([...searchParams]), [searchParams]);
  const pageSelected = useMemo<number>(() => Number(params?.page ?? DEFAULT_CURRENT_PAGE), [params?.page]);
  const limitPage = useMemo<number>(() => Number(params?.limit ?? DEFAULT_LIMIT_PAGE), [params?.limit]);

  const currentRole = JSON.parse(localStorage.getItem(StorageEnum.ROLE)!)[0];
  //#endregion Declare Hook

  //#region Selector
  const isRefreshUserList = useSelector(selectIsRefreshListUser);
  //#endregion Selector

  //#region Declare State
  const [sortBy, setSortBy] = useState<ISortBy[]>(DEFAULT_SORT_USER);
  const [filterParams, setFilterParams] = useState<IQueryParamUser>(FILTER_DEFAULT_VALUES);
  const [searchKey, setSearchKey] = useState<string>(EMPTY_STRING);
  const debounceSearchKey = useDebounce(searchKey, DEFAULT_DEBOUNCE_DELAY_TIME);

  const [paramObject, setParamObject] = useState<IQueryParamUser>({
    page: pageSelected,
    limit: limitPage,
  });
  const [pagination, setPagination] = useState<IPaginationResponse>();

  const [userList, setUserList] = useState<IUser[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);

  const [idUser, setIdUser] = useState<string>(EMPTY_STRING);
  const [selectUser, setSelectUser] = useState<IUser>();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isShowConfirmDelete, setIsShowConfirmDelete] = useState<boolean>(false);
  const [isShowConfirmMakeStatus, setIsShowConfirmMakeStatus] = useState<boolean>(false);
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetListUser(paramObject);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramObject]);

  useEffect(() => {
    if (!isRefreshUserList) return;

    handleGetListUser(paramObject);

    dispatch(userActions.setRefreshUserList(false));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshUserList]);

  useEffect(() => {
    if (pagination || debounceSearchKey) {
      setParamObject({
        ...paramObject,
        page: DEFAULT_CURRENT_PAGE,
        limit: limitPage,
        ...(debounceSearchKey ? { textSearch: debounceSearchKey } : { textSearch: undefined }),
        ...(filterParams ? { ...filterParams } : {}),
      });

      setSearchParams({
        page: String(DEFAULT_CURRENT_PAGE),
        limit: String(limitPage),
        ...(searchKey ? { textSearch: debounceSearchKey } : {}),
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceSearchKey]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetListUser = (params: IQueryParamUser) => {
    setIsLoading(true);

    dispatch(getListUser(params))
      .unwrap()
      .then((res) => {
        const { responses, pagination, totalItems } = res?.data;

        if (!responses) return;

        setUserList(responses);
        setPagination(pagination);

        setTotalItems(totalItems);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleAddUser = () => {
    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(adminRouteAbsolute.createUser);
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(salesAdminRouteAbsolute.createUser);
        break;
      case RoleEnum.SALES_PERSON:
        navigate(salesPersonRouteAbsolute.createUser);
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(operationsManagerRouteAbsolute.createUser);
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(warehouseLeadRouteAbsolute.createUser);
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(warehouseStaffRouteAbsolute.createUser);
        break;
      case RoleEnum.FOREMAN:
        navigate(foremanRouteAbsolute.createUser);
        break;
      case RoleEnum.CREW:
        navigate(crewRouteAbsolute.createUser);
        break;
      case RoleEnum.LOADER:
        navigate(loaderRouteAbsolute.createUser);
        break;
    }
  };

  const handleEdit = (id: string) => {
    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(adminRouteAbsolute.updateUser(id));
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(salesAdminRouteAbsolute.updateUser(id));
        break;
      case RoleEnum.SALES_PERSON:
        navigate(salesPersonRouteAbsolute.updateUser(id));
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(operationsManagerRouteAbsolute.updateUser(id));
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(warehouseLeadRouteAbsolute.updateUser(id));
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(warehouseStaffRouteAbsolute.updateUser(id));
        break;
      case RoleEnum.FOREMAN:
        navigate(foremanRouteAbsolute.updateUser(id));
        break;
      case RoleEnum.CREW:
        navigate(crewRouteAbsolute.updateUser(id));
        break;
      case RoleEnum.LOADER:
        navigate(loaderRouteAbsolute.updateUser(id));
        break;
    }
  };

  const handleView = (id: string, item: IUser) => {
    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(`${adminRouteAbsolute.detailUser(id)}?role=${item.role}`);
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(`${salesAdminRouteAbsolute.detailUser(id)}?role=${item.role}`);
        break;
      case RoleEnum.SALES_PERSON:
        navigate(`${salesPersonRouteAbsolute.detailUser(id)}?role=${item.role}`);
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(`${operationsManagerRouteAbsolute.detailUser(id)}?role=${item.role}`);
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(`${warehouseLeadRouteAbsolute.detailUser(id)}?role=${item.role}`);
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(`${warehouseStaffRouteAbsolute.detailUser(id)}?role=${item.role}`);
        break;
      case RoleEnum.FOREMAN:
        navigate(`${foremanRouteAbsolute.detailUser(id)}?role=${item.role}`);
        break;
      case RoleEnum.CREW:
        navigate(`${crewRouteAbsolute.detailUser(id)}?role=${item.role}`);
        break;
      case RoleEnum.LOADER:
        navigate(`${loaderRouteAbsolute.detailUser(id)}?role=${item.role}`);
        break;
    }
  };

  const handleChangStatus = (item: IUser) => {
    setSelectUser(item);
    setIsShowConfirmMakeStatus(true);
  };

  const handleCloseConfirmMakeStatus = () => {
    setIsShowConfirmMakeStatus(false);
    setSelectUser(undefined);
  };

  const handleConfirmMakeStatus = () => {
    if (!selectUser || !selectUser?.id) return;

    setIsLoading(true);

    const formData = new FormData();
    formData.append('status', selectUser.status === StatusEnum.ACTIVE ? StatusEnum.INACTIVE : StatusEnum.ACTIVE);

    const params: IBodyUpdate<FormData> = {
      id: selectUser.id || EMPTY_STRING,
      body: formData,
    };

    dispatch(updateUser(params))
      .unwrap()
      .then(() => {
        dispatch(userActions.setRefreshUserList(true));
        handleCloseConfirmMakeStatus();
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleDelete = (id: string) => {
    if (!id) return;

    setIdUser(id);
    setIsShowConfirmDelete(true);
  };

  const handleCloseConfirmDelete = () => {
    setIdUser(EMPTY_STRING);
    setIsShowConfirmDelete(false);
  };

  const handleConfirmDelete = () => {
    setIsLoading(true);

    dispatch(deleteUser(idUser))
      .unwrap()
      .then(() => {
        setIdUser(EMPTY_STRING);
        handleCloseConfirmDelete();
        dispatch(userActions.setRefreshUserList(true));
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleSearchChange = (value: string) => {
    setSearchKey(value);
  };

  const handleSort = (sortType: string, name: string) => {
    const updatedSortBy = sortBy.map((item) => ({
      ...item,
      typeSort: item.name === name ? sortType : undefined,
    }));
    setSortBy(updatedSortBy);
    setParamObject({ ...paramObject, sortBy: name, sortType: sortType });
    setSearchParams({ ...params, sortBy: name, sortType: sortType });
  };

  const fetchOptionFilter = useCallback(async (): Promise<IResultCallApiFn<IBaseOption>> => {
    const result: IResultCallApiFn<IBaseOption> = {
      optionsResp: optionsStatusUser,
      hasMoreResp: false,
    };

    return result;
  }, []);

  const fetchOptionRole = useCallback(async (payload: IPayloadCallApiFn): Promise<IResultCallApiFn<IBaseOption>> => {
    const { page, searchKey } = payload;

    const payloadParams: IQueryParamBase = {
      limit: DEFAULT_LIMIT_PAGE,
      page: page,
      ...(searchKey && { textSearch: searchKey }),
    };

    try {
      const resp = await dispatch(getListRole(payloadParams)).unwrap();

      const { responses, pagination } = resp?.data;

      const formatOptions: IBaseOption[] = responses.map((item) => ({
        value: item?.id,
        label: item?.name,
      }));

      const hasMoreResp: boolean = pagination.page < pagination.totalPages || false;

      return {
        optionsResp: formatOptions,
        hasMoreResp: hasMoreResp,
      };
    } catch (_error) {
      return DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA;
    }
  }, []);

  const handleApplyFilter = (values: IQueryParamUser | undefined) => {
    if (!values) {
      setFilterParams({});
      return;
    }

    setFilterParams({
      ...values,
    });

    if (values) {
      setParamObject({
        ...paramObject,
        status: values?.status,
        role: values?.role,
      });
    }
  };

  const handleClearFilter = () => {
    setFilterParams(FILTER_DEFAULT_VALUES);
    setParamObject({
      ...paramObject,
      page: DEFAULT_CURRENT_PAGE,
      limit: limitPage,
      status: undefined,
      role: undefined,
    });
  };

  const handleFilterStatusChange = (
    status: IBaseOption,
    onChange?: (value: IFilterValueChange<IQueryParamUser>) => void
  ) => {
    if (!status) return;

    const { value } = status;

    onChange?.({ name: 'status', value: value });
    setFilterParams({ ...filterParams, status: value });
  };

  const handleFilterRoleChange = (
    role: IBaseOption,
    onChange?: (value: IFilterValueChange<IQueryParamUser>) => void
  ) => {
    if (!role) return;

    const { label } = role;

    onChange?.({ name: 'role', value: label });
    setFilterParams({ ...filterParams, role: label });
  };

  const handlePageChange = (_event: ChangeEvent<unknown>, page: number) => {
    setParamObject({ ...paramObject, page });
    setSearchParams({ ...params, page: String(page), limit: String(limitPage) });
  };

  const handleShowUnderDevelopment = () => {
    setIsShowUnderDevelopment(!isShowUnderDevelopment);
  };
  //#endregion Handle Function

  return (
    <Box id='userPage' className={cx('container')}>
      <BaseToolbar
        title={t('user_title_users_board')}
        total={t('user_title_total_users', { total: totalItems })}
        isBack
      >
        {canCreateUsers && (
          <Box className={cx('button')}>
            <Button
              type='button'
              variant='outlined'
              className={cx('button')}
              onClick={handleAddUser}
              startIcon={<img src={icons.commonIconPlusPrimary} alt={'common_alt_img'} />}
            >
              {t('user_title_add_user')}
            </Button>
          </Box>
        )}

        <BaseInputSearch
          width={300}
          typeStyle='outlined'
          onChange={handleSearchChange}
          borderRadius={100}
          placeholder={t('user_title_search_users')}
        />

        <BaseFilters<IQueryParamUser>
          value={filterParams}
          defaultValue={FILTER_DEFAULT_VALUES}
          onApply={handleApplyFilter}
          onClear={handleClearFilter}
        >
          {({ value, onChange }) => (
            <Stack direction={'column'} width={340} mb={4} gap={6}>
              <BaseSelect
                styleType='contained'
                label={t('user_title_filter_status')}
                placeholder={t('user_title_filter_all_status')}
                callApiFn={fetchOptionFilter}
                value={value?.status}
                onChange={(option) => handleFilterStatusChange(option as any, onChange)}
              />

              <BaseSelect
                isSearch
                type='loadMore'
                styleType='contained'
                label={t('user_title_filter_role')}
                placeholder={t('user_title_filter_all_role')}
                itemNameDefault={value?.role}
                callApiFn={fetchOptionRole}
                value={value?.role}
                onChange={(option) => handleFilterRoleChange(option as any, onChange)}
              />
            </Stack>
          )}
        </BaseFilters>
      </BaseToolbar>

      <Box component={'div'} className={cx('body')}>
        <BaseTable
          dataSource={canViewUsers ? userList : []}
          currentPage={pageSelected}
          totalPage={pagination?.totalPages}
          columns={columnsUsers(
            t,
            sortBy,
            handleSort,
            handleView,
            handleEdit,
            handleChangStatus,
            handleDelete,
            canDetailUsers,
            canEditUsers,
            canDeleteUsers
          )}
          onPageChange={handlePageChange}
          {...(canDetailUsers ? { onRowClick: handleView } : {})}
        />
      </Box>

      {isShowConfirmMakeStatus && (
        <ConfirmModal
          type={selectUser?.status === StatusEnum.ACTIVE ? 'delete' : 'default'}
          icon={selectUser?.status === StatusEnum.ACTIVE ? icons.commonIconStatusIndicator : icons.commonIconCheckMark}
          title={t(
            selectUser?.status === StatusEnum.ACTIVE
              ? 'common_title_confirm_make_status_deactivate'
              : 'common_title_confirm_make_status_activate',
            { ns: 'common/base' }
          )}
          description={t(
            selectUser?.status === StatusEnum.ACTIVE
              ? 'common_desc_confirm_make_status_deactivate'
              : 'common_desc_confirm_make_status_activate',
            { ns: 'common/base', value: 'user' }
          )}
          textConfirm={t(
            selectUser?.status === StatusEnum.ACTIVE ? 'common_action_deactivate' : 'common_action_activate',
            { ns: 'common/base' }
          )}
          onClose={handleCloseConfirmMakeStatus}
          onConfirm={handleConfirmMakeStatus}
        />
      )}

      {isShowConfirmDelete && (
        <ConfirmModal
          type='delete'
          icon={icons.tableIconActionDelete}
          title={t('confirm_delete_user')}
          textConfirm={t('common_action_delete', { ns: 'common/base' })}
          onClose={handleCloseConfirmDelete}
          onConfirm={handleConfirmDelete}
        />
      )}

      {isLoading && <Loading />}

      {isShowUnderDevelopment && <UnderDevelopment onClose={handleShowUnderDevelopment} />}
    </Box>
  );
};

export default User;
